import React, { useEffect, useRef, useState } from "react";

import { ToggleTabsList } from "../../../models";

interface UseToggleTabsProps {
  toggleTabsList: ToggleTabsList;
  currentTab: string;
}

interface UseToggleTabsReturnData {
  value: string;
  setValue: (value: string) => void;
  tabsRef: React.MutableRefObject<(HTMLElement | null)[]>;
}

export const useToggleTabs = ({ toggleTabsList, currentTab }: UseToggleTabsProps): UseToggleTabsReturnData => {
  const [value, setValue] = useState(currentTab);
  const tabsRef = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    setValue(currentTab);
  }, [currentTab]);

  const updateIndicator = (isResize = false): void => {
    const activeTab = tabsRef.current[toggleTabsList.findIndex((tab) => tab.label === value)];
    const activeIndicator = tabsRef.current[toggleTabsList.length];

    if (!activeTab || !activeIndicator) return;

    const jumpToPosition = isResize || activeIndicator.offsetWidth === 0;

    if (jumpToPosition) activeIndicator.style.transition = "none";

    activeIndicator.style.width = `${activeTab.offsetWidth}px`;
    activeIndicator.style.transform = `translateX(${activeTab.offsetLeft}px)`;

    if (jumpToPosition) {
      activeIndicator.getBoundingClientRect();
      activeIndicator.style.transition = "";
    }
  };

  useEffect(() => {
    updateIndicator();
  }, [value, toggleTabsList, tabsRef]);

  useEffect(() => {
    const handleResize = (): void => updateIndicator(true);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [value, toggleTabsList, tabsRef]);

  return {
    value,
    setValue,
    tabsRef,
  };
};
